import { leaderboardResponseSchema, type LeaderboardResponse } from '@soe/protocol';
import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { href } from '../app/useHashRoute.js';
import { Panel } from './Panel.js';
import { StatusBanner } from './StatusBanner.js';

type Load = { kind: 'loading' } | { kind: 'ready'; board: LeaderboardResponse } | { kind: 'error' };

/**
 * The board of registered players. With `limit` it is the Situation Room's short version: the
 * top of the table, your own row if you are lower down, and a link to the whole board.
 */
export function LeaderboardPanel({ limit }: { limit?: number }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    api('GET', '/leaderboard', undefined, { signal: controller.signal })
      .then((body) => {
        setLoad({ kind: 'ready', board: leaderboardResponseSchema.parse(body) });
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoad({ kind: 'error' });
      });
    return () => {
      controller.abort();
    };
  }, []);

  const entries =
    load.kind === 'ready'
      ? load.board.entries.filter((e, i) => limit === undefined || i < limit || e.isYou)
      : [];

  return (
    <Panel title="Leaderboard">
      {load.kind === 'loading' && <StatusBanner kind="info" title="Loading…" />}
      {load.kind === 'error' && <p>The leaderboard could not load.</p>}
      {load.kind === 'ready' && entries.length === 0 && (
        <p>No points yet. Finish a match to get on the board.</p>
      )}
      {entries.length > 0 && (
        <table className="board">
          <thead>
            <tr>
              <th scope="col">Rank</th>
              <th scope="col">Player</th>
              <th scope="col">Points</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.username} className={e.isYou ? 'board__you' : undefined}>
                <td>{e.rank}</td>
                <th scope="row">
                  {e.username}
                  {e.isYou && <span className="board__tag"> (you)</span>}
                </th>
                <td>{e.points}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {limit !== undefined && load.kind === 'ready' && load.board.entries.length > limit && (
        <p className="board__more">
          <a href={href('leaderboard')}>See the full leaderboard</a>
        </p>
      )}
      {load.kind === 'ready' && <p className="board__line">Play more, win more, top the ladder!</p>}
    </Panel>
  );
}
